import { computePosition, offset, flip, shift, size } from "@floating-ui/dom"
import { caretRect } from "./caret"

// Positions a Tribute menu at the textarea caret with Floating UI.
//
// Tribute's own caret math is broken inside scrolled/modal containers, so the
// controllers create it with positionMenu:false — it only toggles the menu's
// display and we own placement here. Floating UI handles the flip (open upward
// near the keyboard), viewport shifting, transformed-ancestor offsets, and
// caps the height to the available space (important for long hashtag lists).
export function followCaret(tribute, field) {
  // Anchor to the trigger char (@/#), not the moving caret — otherwise the
  // menu crawls sideways as the query is typed. The trigger sits one char
  // before the current mention text.
  const anchor = {
    getBoundingClientRect: () => {
      const query = (tribute.current && tribute.current.mentionText) || ""
      const triggerIndex = Math.max(0, field.selectionStart - query.length - 1)
      return caretRect(field, triggerIndex)
    }
  }

  const reposition = () => {
    const menu = tribute.menu
    if (!tribute.isActive || !menu) return

    // Write coordinates to the container, not the menu: Tribute rewrites the
    // menu's inline style on every keystroke, but leaves the container alone,
    // and the menu inherits these custom properties (see application.css).
    const container = tribute.menuContainer || menu.parentElement

    computePosition(anchor, menu, {
      strategy: "fixed",
      placement: "bottom-start",
      middleware: [
        offset(6),
        flip({ padding: 8 }),
        shift({ padding: 8 }),
        size({
          padding: 8,
          apply({ availableHeight }) {
            container.style.setProperty("--tribute-max-h", `${Math.max(140, availableHeight)}px`)
          }
        })
      ]
    }).then(({ x, y }) => {
      container.style.setProperty("--tribute-x", `${x}px`)
      container.style.setProperty("--tribute-y", `${y}px`)
    })
  }

  const onActive = () => {
    reposition()
    window.addEventListener("scroll", reposition, true)
    window.addEventListener("resize", reposition)
  }

  const onInactive = () => {
    window.removeEventListener("scroll", reposition, true)
    window.removeEventListener("resize", reposition)
  }

  field.addEventListener("tribute-active-true", onActive)
  field.addEventListener("tribute-active-false", onInactive)
  field.addEventListener("input", reposition)

  return () => {
    field.removeEventListener("tribute-active-true", onActive)
    field.removeEventListener("tribute-active-false", onInactive)
    field.removeEventListener("input", reposition)
    onInactive()
  }
}
